import React, { useEffect, useRef, useState } from 'react';
import { EndpointNodeCreate } from '@bumble/app/components/endpoint-node/EndpointNodeCreate';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';

interface EndpointCreateProps {
  handleClose: () => void;
}
export const EndpointCreate = ({ handleClose }: EndpointCreateProps) => {
  const api = useApi();
  const containerRef = useRef(null);
  const [showNodeCreate, setShowNodeCreate] = useState(false);

  const [endpointName, setEndpointName] = useState('');
  const [appServiceId, setAppServiceId] = useState('');
  const [selectedDeployment, setSelectedDeployment] = useState('onprem');
  const [selectedEndpointNode, setselectedEndpointNode] = React.useState('');
  const [selectedEndpointNodeGroup, setSelectedEndpointNodeGroup] = React.useState('');
  const [selectedAwsRegion, setSelectedAwsRegion] = React.useState('');
  const [selectedAzureRegion, setSelectedAzureRegion] = React.useState('');
  const [allowedPrincipal, setAllowedPrincipal] = React.useState('');
  const [realSubnets, setRealSubnets] = useState('');
  const [enableBGP, setEnableBGP] = useState(false);
  const [bgpLocalAsn, setBgpLocalAsn] = useState('');
  const [bgpPeerAsn, setBgpPeerAsn] = useState('');
  const [bgpPeerIp, setBgpPeerIp] = useState('');

  const epPost = useMutation(api.createEpApiV1CreateEndpointPost, {
    onSuccess() {
      toast.success('Endpoint created.');
      handleClose();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    switch (selectedDeployment) {
      case 'agent':
        epPost.mutate({
          ep_name: endpointName,
          app_service_id: appServiceId,
          deployment: selectedDeployment,
        });
        break;
      case 'onprem':
        epPost.mutate({
          ep_name: endpointName,
          app_service_id: appServiceId,
          ep_node_id: selectedEndpointNode,
          ep_node_group_id: selectedEndpointNodeGroup,
          deployment: selectedDeployment,
        });
        break;
      case 'aws':
        epPost.mutate({
          ep_name: endpointName,
          app_service_id: appServiceId,
          deployment: selectedDeployment,
          region: selectedAwsRegion,
          consumer_principle: allowedPrincipal,
        });
        break;
      case 'azure': 
        epPost.mutate({
          ep_name: endpointName,
          app_service_id: appServiceId,
          deployment: selectedDeployment,
          region: selectedAzureRegion,
          consumer_principle: allowedPrincipal,
        });
        break;
      case 'network':
        epPost.mutate({
          ep_name: endpointName,
          app_service_id: appServiceId,
          deployment: selectedDeployment,
          ep_node_id: selectedEndpointNode,
          ep_node_group_id: selectedEndpointNodeGroup,
          real_subnets: realSubnets,
          bgp_enabled: enableBGP,
          ...(enableBGP && {
            bgp_local_asn: Number(bgpLocalAsn),
            bgp_peer_asn: Number(bgpPeerAsn),
            bgp_peer_ip: bgpPeerIp,
          }),
        });
        break;

      default:
        break;
    }
  };

  const isDisabled = () => {
    switch (selectedDeployment) {
      case 'agent':
        return !(endpointName && appServiceId);
      case 'onprem':
        return !(endpointName && appServiceId && selectedEndpointNode && selectedEndpointNodeGroup);
      case 'aws':
        return !(endpointName && appServiceId && selectedAwsRegion && allowedPrincipal);
      case 'azure':
        return !(endpointName && appServiceId && selectedAzureRegion && allowedPrincipal);
      case 'network':
        if (enableBGP) {
          return !(
            endpointName &&
            appServiceId &&
            realSubnets &&
            selectedEndpointNode &&
            selectedEndpointNodeGroup &&
            bgpLocalAsn &&
            bgpPeerAsn &&
            bgpPeerIp
          );
        }
        if (!enableBGP) {
          return !(
            endpointName &&
            appServiceId &&
            realSubnets &&
            selectedEndpointNode &&
            selectedEndpointNodeGroup
          );
        }
        return true;
      default:
        return true;
    }
  };

  const {
    isLoading: groupIsLoading,
    data: groupData,
    refetch: groupRefetch,
  } = useQuery(
    ['endpointNodeGroups'],
    api.listEpNodeGroupApiV1ListEndpointNodeGroupGet.bind(null, {}),
    { enabled: true },
  );

  const {
    // isLoading: nodeIsLoading,
    data: nodeData,
    refetch: nodeRefetch,
  } = useQuery(
    ['endpointNode'],
    api.listEpNodeApiV1ListEndpointNodeGet.bind(null, {
      ep_node_group_id: selectedEndpointNodeGroup,
    }),
    {
      enabled: false,
    },
  );

  useEffect(() => {
    if (selectedEndpointNodeGroup) {
      nodeRefetch();
    }
  }, [nodeRefetch, selectedEndpointNodeGroup]);

  const handleCreateModalClose = () => {
    setShowNodeCreate(false);
    groupRefetch();
    if (selectedEndpointNodeGroup) {
      nodeRefetch();
    }
  };

  const handleEndpointNode = (event: SelectChangeEvent) => {
    setselectedEndpointNode(event.target.value as string);
  };

  const handleEndpointNodeGroup = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setSelectedEndpointNodeGroup(value);
    setselectedEndpointNode('');
  };
  const { isLoading: awsLoading, data: awsData } = useQuery(
    ['awsRegions'],
    api.listAwsRegionsApiV1ListAwsRegionsGet.bind(null, {}),
    { enabled: true },
  );
  const { isLoading: azureLoading, data: azureData } = useQuery(
    ['azureRegions'],
    api.listAzureRegionsApiV1ListAzureRegionsGet.bind(null, {}),
    { enabled: true },
  );

  if (groupIsLoading || awsLoading || azureLoading) {
    return <Loading />;
  }
  return (
    <Paper ref={containerRef} sx={{ overflow: 'hidden' }} elevation={0}>
      <Slide
        direction="right"
        in={!showNodeCreate}
        appear={false}
        container={containerRef.current}
        mountOnEnter
        unmountOnExit
      >
        <Box pt={2}>
          <Box>
            <TextField
              fullWidth
              id="endpoint-name"
              label="Endpoint Name"
              variant="outlined"
              sx={{ mb: 4 }}
              value={endpointName}
              onChange={(e) => setEndpointName(e.target.value)}
              autoFocus
            />
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  id="service-app-name"
                  label="App Service Id"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={appServiceId}
                  onChange={(e) => setAppServiceId(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel id="deployment-label">Location</InputLabel>
                  <Select
                    labelId="deployment-label"
                    id="deployment"
                    label="Location"
                    value={selectedDeployment}
                    onChange={(e) => setSelectedDeployment(e.target.value)}
                  >
                    <MenuItem value="onprem">On-Prem</MenuItem>
                    <MenuItem value="agent">Agent</MenuItem>
                    <MenuItem value="aws">AWS</MenuItem>
                    <MenuItem value="azure">Azure</MenuItem>
                    <MenuItem value="network">Network</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {selectedDeployment === 'aws' && (
              <Box>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel id="aws-regions-label">AWS Region</InputLabel>
                  <Select
                    labelId="aws-regions-label"
                    id="aws-regions"
                    label="AWS Region"
                    value={selectedAwsRegion}
                    onChange={(e) => setSelectedAwsRegion(e.target.value)}
                  >
                    {awsData?.data.map((region: string) => (
                      <MenuItem value={region} key={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  id="allowed-principal"
                  label="Allowed Principal"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={allowedPrincipal}
                  onChange={(e) => setAllowedPrincipal(e.target.value)}
                  autoFocus
                />
              </Box>
            )}
            {selectedDeployment === 'azure' && (
              <Box>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel id="azure-regions-label">Azure Region</InputLabel>
                  <Select
                    labelId="azure-regions-label"
                    id="azure-regions"
                    label="Azure Region"
                    value={selectedAzureRegion}
                    onChange={(e) => setSelectedAzureRegion(e.target.value)}
                  >
                    {azureData?.data.map((region: string) => (
                      <MenuItem value={region} key={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  id="allowed-principal"
                  label="Azure Subscription ID"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={allowedPrincipal}
                  onChange={(e) => setAllowedPrincipal(e.target.value)}
                  autoFocus
                />
              </Box>
            )}
            {selectedDeployment === 'onprem' && (
              <Grid container>
                <Grid item xs={12} md={6} display="flex" alignItems="center">
                  {groupData && groupData?.data.length > 0 ? (
                    <Box sx={{ width: '100%' }}>
                      <FormControl fullWidth sx={{ mb: 4 }}>
                        <InputLabel id="endpoint-node-group-label">Endpoint Node Groups</InputLabel>
                        <Select
                          labelId="endpoint-node-group-label"
                          id="endpoint-node-group"
                          label="Endpoint Node Groups"
                          value={selectedEndpointNodeGroup}
                          onChange={handleEndpointNodeGroup}
                        >
                          <MenuItem value="">Select an Endpoint Node Group</MenuItem>
                          {groupData?.data.map(({ ep_node_group_name, ep_node_group_id }) => (
                            <MenuItem value={ep_node_group_id} key={ep_node_group_id}>
                              {ep_node_group_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth sx={{ mb: 4 }}>
                        <InputLabel id="endpoint-node-label">Endpoint Nodes</InputLabel>
                        <Select
                          labelId="endpoint-node-label"
                          id="endpoint-node"
                          label="Endpoint Nodes"
                          value={selectedEndpointNode}
                          onChange={handleEndpointNode}
                          disabled={!selectedEndpointNodeGroup}
                        >
                          <MenuItem value="">Select an Endpoint Node</MenuItem>
                          {nodeData &&
                            nodeData?.data.map(({ ep_node_name, ep_node_id }) => (
                              <MenuItem value={ep_node_id} key={ep_node_id}>
                                {ep_node_name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No Endpoint Node Groups found.
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Button
                    variant="text"
                    onClick={() => setShowNodeCreate(true)}
                    startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                  >
                    New Endpoint Node Group
                  </Button>
                </Grid>
              </Grid>
            )}
            {selectedDeployment === 'network' && (
              <Box>
                <TextField
                  fullWidth
                  id="real-subnets"
                  label="Real subnet CIDR lists"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={realSubnets}
                  onChange={(e) => setRealSubnets(e.target.value)}
                />
                <Box mb={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableBGP}
                        onChange={(e) => setEnableBGP(e.target.checked)}
                      />
                    }
                    label="Enable BGP"
                  />
                  {enableBGP && (
                    <>
                      <TextField
                        fullWidth
                        id="bgp-local-asn"
                        label="Bumblebee side ASN"
                        variant="outlined"
                        sx={{ mb: 4, mt: 4 }}
                        value={bgpLocalAsn}
                        onChange={(e) => setBgpLocalAsn(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        id="bgp-peer-asn"
                        label="Customer side ASN"
                        variant="outlined"
                        sx={{ mb: 4 }}
                        value={bgpPeerAsn}
                        onChange={(e) => setBgpPeerAsn(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        id="bgp-peer-ip"
                        label="Customer side BGP peer IP address"
                        variant="outlined"
                        sx={{ mb: 4 }}
                        value={bgpPeerIp}
                        onChange={(e) => setBgpPeerIp(e.target.value)}
                      />
                    </>
                  )}
                </Box>
                {groupData && groupData?.data.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <FormControl fullWidth sx={{ mb: 4 }}>
                      <InputLabel id="endpoint-node-group-label">Endpoint Node Groups</InputLabel>
                      <Select
                        labelId="endpoint-node-group-label"
                        id="endpoint-node-group"
                        label="Endpoint Node Groups"
                        value={selectedEndpointNodeGroup}
                        onChange={handleEndpointNodeGroup}
                      >
                        <MenuItem value="">Select an Endpoint Node Group</MenuItem>
                        {groupData?.data.map(({ ep_node_group_name, ep_node_group_id }) => (
                          <MenuItem value={ep_node_group_id} key={ep_node_group_id}>
                            {ep_node_group_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 4 }}>
                      <InputLabel id="endpoint-node-label">Endpoint Nodes</InputLabel>
                      <Select
                        labelId="endpoint-node-label"
                        id="endpoint-node"
                        label="Endpoint Nodes"
                        value={selectedEndpointNode}
                        onChange={handleEndpointNode}
                        disabled={!selectedEndpointNodeGroup}
                      >
                        <MenuItem value="">Select an Endpoint Node</MenuItem>
                        {nodeData &&
                          nodeData?.data.map(({ ep_node_name, ep_node_id }) => (
                            <MenuItem value={ep_node_id} key={ep_node_id}>
                              {ep_node_name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No Endpoint Node Groups found.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" sx={{ mt: 10 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                onClick={handleSubmit}
                disabled={isDisabled()}
              >
                Create
              </Button>
            </Box>
          </Box>
        </Box>
      </Slide>
      <Slide
        direction="left"
        in={showNodeCreate}
        container={containerRef.current}
        mountOnEnter
        unmountOnExit
      >
        <Box>
          <EndpointNodeCreate onCancel={handleCreateModalClose} />
          <Divider sx={{ my: 4 }} />
          <Button variant="text" onClick={handleCreateModalClose} startIcon={<ArrowBackIcon />}>
            Return to Create Endpoint
          </Button>
        </Box>
      </Slide>
    </Paper>
  );
};
